import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { 
  Search, Crown, MoreVertical, Loader2, User, Building2,
  Mail, Calendar, Shield, RefreshCw, ChevronLeft, ChevronRight,
  UserCog, Eye, Trash2, Edit2, Filter, Users, AlertTriangle,
  ArrowUp, ArrowDown, ArrowUpDown, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import CreateUserModal from './CreateUserModal';

// Role badge colors
const getRoleBadge = (role) => {
  const roleConfig = {
    admin: { label: 'System Admin', className: 'bg-purple-100 text-purple-700' },
    owner: { label: 'Owner', className: 'bg-blue-100 text-blue-700' },
    product_manager: { label: 'Product Manager', className: 'bg-green-100 text-green-700' },
    billing_admin: { label: 'Billing Manager', className: 'bg-amber-100 text-amber-700' },
    collaborator: { label: 'Contributor', className: 'bg-gray-100 text-gray-700' },
    member: { label: 'Member', className: '' },
  };
  return roleConfig[role] || { label: role || 'Unknown', className: '' };
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

const AdminUsersContent = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Create user modal
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (page * pageSize).toString(),
        limit: pageSize.toString(),
        exclude_test: 'true',
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter) params.append('role', roleFilter);
      
      const res = await api.get(`/api/admin/metrics/users?${params}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Sort users
  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'name':
          aVal = (a.name || a.email || '').toLowerCase();
          bVal = (b.name || b.email || '').toLowerCase();
          break;
        case 'organization':
          aVal = (a.organization_name || '').toLowerCase();
          bVal = (b.organization_name || '').toLowerCase();
          break;
        case 'role':
          aVal = a.team_role || a.role || '';
          bVal = b.team_role || b.role || '';
          break;
        case 'joined':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [users, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => setPage(0), 300));
  };

  const handleViewUser = async (user) => {
    try {
      const res = await api.get(`/api/admin/metrics/users/${user.user_id}`);
      setSelectedUser(res.data);
      setShowDetailModal(true);
    } catch (err) {
      toast.error('Failed to load user details');
    }
  };

  const handleEditRole = (user) => {
    setSelectedUser({ user });
    setNewRole(user.team_role || user.role);
    setShowRoleModal(true);
  };

  const handleSaveRole = async () => {
    if (!selectedUser?.user?.user_id || !newRole) return;
    
    setSaving(true);
    try {
      await api.put(`/api/admin/metrics/users/${selectedUser.user.user_id}/role`, { role: newRole });
      toast.success('Role updated successfully');
      setShowRoleModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser?.user?.user_id) return;
    
    setSaving(true);
    try {
      await api.delete(`/api/admin/metrics/users/${selectedUser.user.user_id}`);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };

  const handleForceDeleteUser = async () => {
    if (!selectedUser?.user?.user_id) return;
    
    setSaving(true);
    try {
      await api.delete(`/api/admin/metrics/users/${selectedUser.user.user_id}/force`);
      toast.success('User permanently deleted');
      setShowForceDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force delete user');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Stats Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <p className={cn("text-sm", colors.textMuted)}>
          {total} total users (excluding test accounts)
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchUsers} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button 
            size="sm" 
            onClick={() => setShowCreateUserModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className={cn("p-4", colors.bgCard)}>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          
          <Select value={roleFilter || 'all'} onValueChange={(v) => { setRoleFilter(v === 'all' ? '' : v); setPage(0); }}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="product_manager">Product Manager</SelectItem>
              <SelectItem value="billing_admin">Billing Admin</SelectItem>
              <SelectItem value="collaborator">Collaborator</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Users Table */}
      <Card className={cn("overflow-hidden", colors.bgCard)}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className={colors.textMuted}>Loading users...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <Users className={cn("w-12 h-12 mx-auto mb-4 opacity-50", colors.textMuted)} />
            <p className={colors.textMuted}>No users found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={cn("border-b", colors.bgSecondary, colors.borderPrimary)}>
                  <tr>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">User{renderSortIcon('name')}</div>
                    </th>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('organization')}
                    >
                      <div className="flex items-center">Organization{renderSortIcon('organization')}</div>
                    </th>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('role')}
                    >
                      <div className="flex items-center">Role{renderSortIcon('role')}</div>
                    </th>
                    <th 
                      className={cn("text-left px-4 py-3 text-xs font-medium uppercase cursor-pointer select-none hover:bg-black/5", colors.textMuted)}
                      onClick={() => handleSort('joined')}
                    >
                      <div className="flex items-center">Joined{renderSortIcon('joined')}</div>
                    </th>
                    <th className={cn("text-right px-4 py-3 text-xs font-medium uppercase", colors.textMuted)}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedUsers.map((user) => {
                    const roleInfo = getRoleBadge(user.team_role || user.role);
                    return (
                      <tr key={user.user_id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={cn("w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium", colors.bgSecondary)}>
                              {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className={cn("font-medium", colors.textPrimary)}>{user.name || 'No name'}</p>
                              <p className={cn("text-xs", colors.textMuted)}>{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className={cn("text-sm", colors.textPrimary)}>{user.organization_name || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={roleInfo.className}>{roleInfo.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn("text-sm", colors.textMuted)}>{formatDate(user.created_at)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewUser(user)}>
                                  <Eye className="w-4 h-4 mr-2" /> View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditRole(user)}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Edit Role
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => { setSelectedUser({ user }); setShowDeleteModal(true); }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete User
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={cn("flex items-center justify-between px-4 py-3 border-t", colors.borderPrimary)}>
              <p className={cn("text-sm", colors.textMuted)}>
                Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className={cn("text-sm", colors.textMuted)}>Page {page + 1} of {totalPages || 1}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* View Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className={cn("max-w-lg", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={colors.textMuted}>Name</Label>
                  <p className={colors.textPrimary}>{selectedUser.user?.name || 'N/A'}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>Email</Label>
                  <p className={colors.textPrimary}>{selectedUser.user?.email}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>User ID</Label>
                  <p className={cn("font-mono text-xs", colors.textMuted)}>{selectedUser.user?.user_id}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>Role</Label>
                  <Badge className={getRoleBadge(selectedUser.user?.team_role || selectedUser.user?.role).className}>
                    {getRoleBadge(selectedUser.user?.team_role || selectedUser.user?.role).label}
                  </Badge>
                </div>
                <div>
                  <Label className={colors.textMuted}>Organization</Label>
                  <p className={colors.textPrimary}>{selectedUser.user?.organization_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className={colors.textMuted}>Joined</Label>
                  <p className={colors.textPrimary}>{formatDate(selectedUser.user?.created_at)}</p>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setShowDetailModal(false); handleEditRole(selectedUser.user); }}>
                  <Edit2 className="w-4 h-4 mr-2" /> Edit Role
                </Button>
                <Button variant="outline" size="sm" className="text-red-600" onClick={() => { setShowDetailModal(false); setShowDeleteModal(true); }}>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Role Edit Modal */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className={cn("max-w-md", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
            <DialogDescription>Change role for {selectedUser?.user?.email}</DialogDescription>
          </DialogHeader>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger>
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">System Admin</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="product_manager">Product Manager</SelectItem>
              <SelectItem value="billing_admin">Billing Admin</SelectItem>
              <SelectItem value="collaborator">Collaborator</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleModal(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className={cn("max-w-md", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUser?.user?.email}? This will remove them from their organization.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onSuccess={fetchUsers}
      />
    </div>
  );
};

export default AdminUsersContent;
